// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { ContactFilter, ContactSortProperty } from '@/contact/api/get-contacts';
import { ContactTableRow } from '@/contact/components/contact-table-row';
import { useContacts } from '@/contact/hooks/use-contacts';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

interface Props {
  sort: Sort<ContactSortProperty>;
  filter?: ContactFilter;
  page?: number;
  pageSize?: number;
  hideCompany?: boolean;
  onSort: (property: ContactSortProperty) => void;
  onPageChange?: (page: number) => void;
}

export function ContactTable(props: Props) {
  const page = props.page ?? 1;
  const pageSize = props.pageSize ?? 25;

  const { contacts, totalCount } = useContacts({
    sort: props.sort,
    filter: props.filter,
    page,
    pageSize,
  });

  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  const downloadURL = props.hideCompany
    ? undefined
    : new URL('/v1/export/contacts', import.meta.env.VITE_API_URL);

  const createSortHandler = (column: ContactSortProperty) => () => {
    props.onSort(column);
  };

  if (contacts.length === 0) {
    return (
      <EmptyState title="No contacts found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            Add a contact to start tracking people at your companies
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={getSortDirection('name')}>
                <TableSortLabel
                  active={isLabelActive('name')}
                  direction={getLabelDirection('name')}
                  onClick={createSortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              {!props.hideCompany && (
                <TableCell sortDirection={getSortDirection('customer')}>
                  <TableSortLabel
                    active={isLabelActive('customer')}
                    direction={getLabelDirection('customer')}
                    onClick={createSortHandler('customer')}
                  >
                    Company
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sortDirection={getSortDirection('title')}>
                <TableSortLabel
                  active={isLabelActive('title')}
                  direction={getLabelDirection('title')}
                  onClick={createSortHandler('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={getSortDirection('email')}>
                <TableSortLabel
                  active={isLabelActive('email')}
                  direction={getLabelDirection('email')}
                  onClick={createSortHandler('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={getSortDirection('phone')}>
                <TableSortLabel
                  active={isLabelActive('phone')}
                  direction={getLabelDirection('phone')}
                  onClick={createSortHandler('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={getSortDirection('lastUpdatedAt')}>
                <TableSortLabel
                  active={isLabelActive('lastUpdatedAt')}
                  direction={getLabelDirection('lastUpdatedAt')}
                  onClick={createSortHandler('lastUpdatedAt')}
                >
                  Last updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <ContactTableRow
                key={contact.id}
                contact={contact}
                hideCompany={props.hideCompany}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {props.onPageChange && (
        <TablePaginationFooter
          pagination={{
            page,
            pageSize,
            onPageChange: props.onPageChange,
          }}
          totalCount={totalCount}
          entityOptions={{ singular: 'contact', includeCount: false }}
          downloadURL={downloadURL}
          downloadLabel="Export contact data"
        />
      )}
    </>
  );
}
